import { useState } from "react";
import { useLocation } from "wouter";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2, Sparkles, Wand2 } from "lucide-react";
import { Link } from "wouter";

export function CustomPromptPage() {
  const [, setLocation] = useLocation();
  
  const [name, setName] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [userPrompt, setUserPrompt] = useState("");
  
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!systemPrompt.trim()) {
      setError("请输入系统提示词（生成风格指令）");
      return;
    }

    if (!userPrompt.trim()) {
      setError("请输入用户内容");
      return;
    }

    setSubmitting(true);

    const response = await api.projects.create({
      name: name.trim() || undefined,
      templateId: 'custom-prompt',
      inputText: userPrompt.trim(),
      systemPrompt: systemPrompt.trim(),
    });

    if (response.success && response.data) {
      setLocation(`/projects/${response.data.id}`);
    } else {
      setError(response.error || "创建项目失败");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Link href="/">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-violet-500" />
              自定义提示词生成
            </h1>
            <p className="text-sm text-muted-foreground">使用您自己的提示词来生成图片</p>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="container mx-auto px-4 py-8 max-w-3xl">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* System Prompt */}
          <section>
            <Label htmlFor="systemPrompt" className="text-lg font-semibold">
              系统提示词（生成风格）
            </Label>
            <p className="text-sm text-muted-foreground mt-1 mb-2">
              定义图片生成的风格、比例、语言等指令
            </p>
            <textarea
              id="systemPrompt"
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              placeholder="例如：生成一张油画风格的图片，比例为16:9，色彩鲜艳，有强烈的光影对比..."
              rows={4}
              className="w-full rounded-lg border border-input bg-background px-4 py-3 text-sm shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </section>

          {/* Project Name */}
          <section>
            <Label htmlFor="name" className="text-lg font-semibold">
              项目名称 <span className="text-sm font-normal text-muted-foreground">(选填)</span>
            </Label>
            <p className="text-sm text-muted-foreground mt-1 mb-2">
              不填写将由 AI 自动生成名称
            </p>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="为您的项目起个名字..."
            />
          </section>

          {/* User Prompt */}
          <section>
            <Label htmlFor="userPrompt" className="text-lg font-semibold">
              图片内容描述
            </Label>
            <p className="text-sm text-muted-foreground mt-1 mb-2">
              描述您想要生成的图片内容
            </p>
            <textarea
              id="userPrompt"
              value={userPrompt}
              onChange={(e) => setUserPrompt(e.target.value)}
              placeholder="输入您想要生成的图片内容描述...&#10;&#10;例如：一只小猫坐在窗台上，望着窗外的雨滴，背景是温暖的室内灯光..."
              rows={6}
              className="w-full rounded-lg border border-input bg-background px-4 py-3 text-sm shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </section>

          {/* Error message */}
          {error && (
            <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/30">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Submit */}
          <div className="flex items-center gap-4">
            <Button
              type="submit"
              size="lg"
              disabled={submitting}
              className="gap-2"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Sparkles className="w-5 h-5" />
              )}
              {submitting ? "生成中..." : "开始生成"}
            </Button>
            
            <Link href="/">
              <Button type="button" variant="outline" size="lg">
                取消
              </Button>
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
}
